/**
 * USB HID 轮询率分析器
 * 主入口文件
 */

import './styles/main.css';
import { USBHIDAnalyzer } from './core/analyzer';
import { UIRenderer } from './ui/renderer';
import type { AnalysisResult } from './types';

class App {
  private analyzer: USBHIDAnalyzer;
  private renderer: UIRenderer;
  private analysisResult: AnalysisResult | null = null;

  constructor() {
    this.analyzer = new USBHIDAnalyzer();
    this.renderer = new UIRenderer();
  }

  /**
   * 初始化应用
   */
  init(): void {
    // 检查 WebHID 支持
    if (!('hid' in navigator)) {
      this.showUnsupportedMessage();
      return;
    }

    // 初始化 UI
    this.renderer.initializeApp();

    // 绑定事件
    this.bindEvents();
    this.bindAnalyzerEvents();
  }

  /**
   * 显示不支持消息
   */
  private showUnsupportedMessage(): void {
    const app = document.getElementById('app');
    if (app) {
      app.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          padding: 2rem;
          text-align: center;
          font-family: var(--font-mono);
          background: var(--bg-hard);
          color: var(--fg);
        ">
          <h1 style="font-size: 2rem; margin-bottom: 1rem; color: var(--red);">
            ⚠️ 浏览器不支持 WebHID
          </h1>
          <p style="max-width: 500px; line-height: 1.6; color: var(--fg3);">
            此应用需要 WebHID API 来访问 USB HID 设备。<br><br>
            请使用最新版本的 <strong>Chrome</strong>、<strong>Edge</strong> 或 <strong>Opera</strong> 浏览器。
          </p>
          <p style="margin-top: 2rem; font-size: 0.875rem; color: var(--fg4);">
            Firefox 和 Safari 目前不支持 WebHID。
          </p>
        </div>
      `;
    }
  }

  /**
   * 绑定 DOM 事件
   */
  private bindEvents(): void {
    // 使用事件委托
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;

      // 连接按钮
      if (target.closest('#connectBtn')) {
        this.handleConnect();
      }

      // 断开按钮
      if (target.closest('#disconnectBtn')) {
        this.handleDisconnect();
      }

      // 开始分析按钮
      if (target.closest('#startBtn')) {
        this.handleStartAnalysis();
      }

      // 再测一次按钮
      if (target.closest('#rerunBtn')) {
        this.handleStartAnalysis();
      }

      // 导出按钮
      if (target.closest('#exportBtn')) {
        this.handleExport();
      }
    });
  }

  /**
   * 绑定分析器事件
   */
  private bindAnalyzerEvents(): void {
    this.analyzer.on('device:connected', (device) => {
      this.renderer.updateStatus('connected', '已连接');
      this.renderer.updateButtons(true, false);
      this.renderer.showDeviceCard(device);
    });

    this.analyzer.on('device:disconnected', () => {
      this.renderer.updateStatus('idle', '等待连接');
      this.renderer.updateButtons(false, false);
      this.renderer.hideAnalysisCards();
      this.analysisResult = null;
    });

    this.analyzer.on('capture:start', () => {
      this.renderer.updateStatus('testing', '测试中...');
      this.renderer.updateButtons(true, true);
      this.renderer.showCaptureAnimation();
    });

    this.analyzer.on('capture:progress', ({ count, realtimeRate }) => {
      this.renderer.updateCaptureProgress(count, realtimeRate);
    });

    this.analyzer.on('capture:complete', () => {
      this.renderer.updateStatus('analyzing', '分析中...');
      this.renderer.hideCaptureAnimation();

      // 延迟分析以让 UI 更新
      setTimeout(() => {
        this.analysisResult = this.analyzer.analyze();

        if (this.analysisResult) {
          this.renderer.updateStatus(
            'done',
            `${this.analysisResult.metrics.effectiveRate.toFixed(0)} Hz`
          );
          this.renderer.updateButtons(true, false);
          this.renderer.renderAnalysisResult(this.analysisResult);

          // 渲染历史记录
          const history = this.analyzer.getMeasurementHistory();
          this.renderer.renderMeasurementHistory(history);
        } else {
          this.renderer.updateStatus('connected', '已连接');
          this.renderer.updateButtons(true, false);
        }
      }, 100);
    });

    this.analyzer.on('error', (error) => {
      console.error('Analyzer error:', error);
      alert(`错误: ${error.message}`);

      this.renderer.hideCaptureAnimation();
      this.renderer.updateStatus('connected', '已连接');
      this.renderer.updateButtons(true, false);
    });
  }

  /**
   * 处理连接
   */
  private async handleConnect(): Promise<void> {
    this.renderer.updateStatus('connecting', '连接中...');

    const success = await this.analyzer.connect();

    if (!success) {
      this.renderer.updateStatus('idle', '未连接设备');
    }
  }

  /**
   * 处理断开
   */
  private handleDisconnect(): void {
    this.analyzer.disconnect();
  }

  /**
   * 处理开始分析
   */
  private async handleStartAnalysis(): Promise<void> {
    await this.analyzer.startCapture();
  }

  /**
   * 处理导出
   */
  private handleExport(): void {
    if (this.analysisResult) {
      this.analyzer.exportData(this.analysisResult);
    }
  }
}

// 启动应用
document.addEventListener('DOMContentLoaded', () => {
  const app = new App();
  app.init();
});
